import { useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeleteInvoiceDialog({ open, onOpenChange, invoice, onSuccess, apiEndpoint = "invoices" }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    if (!invoice?.numero) return;

    try {
      setLoading(true);
      
      await axios.delete(
        `${API}/${apiEndpoint}/${encodeURIComponent(invoice.numero)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Facture ${invoice.numero} supprimée avec succès`);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) return null;

  // Determine label based on endpoint
  const entityLabel = apiEndpoint === "supplier-invoices" ? "Fournisseur" : "Client";
  const entityName = apiEndpoint === "supplier-invoices" ? invoice.fournisseur : invoice.client;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="delete-invoice-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            Supprimer la facture ?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                Êtes-vous sûr de vouloir supprimer la facture <strong className="text-foreground">{invoice.numero}</strong> ?
              </p>
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                <p><strong>{entityLabel} :</strong> {entityName}</p>
                <p><strong>Montant TTC :</strong> {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(invoice.total_ttc || 0)}</p>
              </div>
              <p className="text-red-600 font-medium">
                Cette action est irréversible. Le PDF associé sera également supprimé.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Annuler</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="confirm-delete-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Suppression...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
